import React, { useCallback, useContext, useEffect, useState } from 'react';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import useDatasetDef from 'components/Inputs/FilterBuilder/useDatasetDef';
import useGetFieldLabel from 'hooks/useGetFieldLabel';

const useFieldGreenList = ({
  entityDataset,
  primaryField,
  editingEntity,
}: {
  entityDataset: string | undefined;
  primaryField: string | undefined;
  editingEntity?: EntityDetails.Entity;
}) => {
  const { entityDefinitions, isLoading } = useContext(EntityDefinitionsContext);
  const datasetDef = useDatasetDef(entityDataset);

  const buildGreenList = useCallback(() => {
    if (datasetDef === undefined || isLoading) {
      return [];
    }

    const datasetFields = datasetDef.fields
      .filter((f) => f.type === 'text')
      .map((f) => f.field);
    return datasetFields
      .filter((f1) => {
        // Not used in primary field
        return primaryField !== f1;
      })
      .filter((f1) => {
        // Not used in another entity def
        return !entityDefinitions
          .filter((e) => {
            if (!editingEntity) {
              return true;
            }
            return editingEntity.id !== e.id;
          })
          .some((e) => {
            const isPrimary = e.primaryField === f1;
            const isSecondary = e.secondaryFields.some((f2) => f2 === f1);
            return isPrimary || isSecondary;
          });
      });
  }, [datasetDef, editingEntity, entityDefinitions, isLoading, primaryField]);
  const [greenList, setGreenList] = useState<string[]>(buildGreenList);
  useEffect(() => {
    setGreenList(buildGreenList());
  }, [buildGreenList]);

  return greenList;
};

const useSecondaryFieldOptions = ({
  entityDataset,
  primaryField,
  secondaryFields,
  setSecondaryFields,
  editingEntity,
}: {
  entityDataset: string | undefined;
  primaryField: string | undefined;
  secondaryFields: string[];
  setSecondaryFields: React.Dispatch<React.SetStateAction<string[]>>;
  editingEntity?: EntityDetails.Entity;
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const fieldGreenList = useFieldGreenList({
    entityDataset,
    primaryField,
    editingEntity,
  });
  const buildOptions = useCallback(() => {
    if (!entityDataset) {
      return [];
    }

    return fieldGreenList.map((f1) => {
      return {
        value: f1,
        label: getFieldLabel({ field: f1, dataType: entityDataset }),
        isSelected: secondaryFields.some((f2) => f2 === f1),
        onSelected: () => {
          setSecondaryFields((current) => {
            const isSelected = current.some((f2) => f2 === f1);
            if (isSelected) {
              return current.filter((f2) => f2 !== f1);
            } else {
              return [...current, f1];
            }
          });
        },
      };
    });
  }, [
    fieldGreenList,
    getFieldLabel,
    entityDataset,
    secondaryFields,
    setSecondaryFields,
  ]);
  const [options, setOptions] = useState<DropdownOption[]>(buildOptions);
  useEffect(() => {
    setOptions(buildOptions());
  }, [buildOptions]);

  return options;
};

export default useSecondaryFieldOptions;
